import {
  USER_LOAN_ESTIMATION_TYPE,
  USER_LOAN_PROGRAM,
  USER_OCCUPANCY,
} from '@/config/constants';

export type RateWatchPropertyUse = 'primary' | 'investment';

export type RateWatchWizardForm = {
  propertyUse: RateWatchPropertyUse;
  currentRate: number;
  street: string;
  city: string;
  state: string;
  zip: string;
  propertyValue: number;
  balance: number;
  termYears: number;
  monthlyPayment: number;
  program: USER_LOAN_PROGRAM;
  spreadBps: number;
};

export function occupancyForPropertyUse(
  propertyUse: RateWatchPropertyUse
): USER_OCCUPANCY {
  return propertyUse === 'investment'
    ? USER_OCCUPANCY.INVESTMENT_PROPERTY
    : USER_OCCUPANCY.PRIMARY_RESIDENCE;
}

/** Maps occupancy + program to backend rate-alert loanProgram slug. */
export function loanProgramForRateAlert(
  propertyUse: RateWatchPropertyUse,
  program: USER_LOAN_PROGRAM
): string {
  if (propertyUse === 'investment') return 'dscr';
  return String(program).toLowerCase();
}

export function defaultProgramForPropertyUse(
  propertyUse: RateWatchPropertyUse
): USER_LOAN_PROGRAM {
  return propertyUse === 'investment'
    ? USER_LOAN_PROGRAM.DSCR
    : USER_LOAN_PROGRAM.CONVENTIONAL;
}

export function userProfilePatchForRateWatch(
  propertyUse: RateWatchPropertyUse,
  program: USER_LOAN_PROGRAM
) {
  const occupancy = occupancyForPropertyUse(propertyUse);
  return {
    onboardingCompleted: true as const,
    onboardingIntent: 'rate_watch' as const,
    loanEstimationType: USER_LOAN_ESTIMATION_TYPE.REFINANCE,
    occupancy,
    loanProgram:
      propertyUse === 'investment' ? USER_LOAN_PROGRAM.DSCR : program,
  };
}

export function buildRateWatchProductPayload(form: RateWatchWizardForm) {
  const fullAddress = `${form.street}, ${form.city}, ${form.state} ${form.zip}`;
  const program = defaultProgramForPropertyUse(form.propertyUse);
  const selectedProgram =
    form.propertyUse === 'investment' ? program : form.program;

  return {
    name: fullAddress,
    price: form.propertyValue,
    transactionMode: 'refinance' as const,
    savedLoanType: selectedProgram,
    address: {
      street: form.street,
      city: form.city,
      state: form.state,
      zip: form.zip,
      fullAddress,
    },
    refinance: {
      currentLoan: {
        rate: form.currentRate,
        balance: form.balance,
        termYears: form.termYears,
        monthlyPayment: form.monthlyPayment,
        escrowTaxes: 0,
        escrowInsurance: 0,
        escrowHoa: 0,
      },
      property: {
        street: form.street,
        city: form.city,
        state: form.state,
        zip: form.zip,
        fullAddress,
        value: form.propertyValue,
        propertyType: 'Single Family',
      },
      newLoan: {
        termYears: form.termYears || 30,
        creditScore: 720,
        cashOutAmount: 0,
        requestedLoanAmount: 0,
      },
      selection: {
        program: selectedProgram,
        refiType: 'rate_and_term',
        occupancy: occupancyForPropertyUse(form.propertyUse),
      },
    },
  };
}

export function needsOnboarding(
  user:
    | {
        onboardingCompleted?: boolean | null;
      }
    | null
    | undefined
): boolean {
  return user?.onboardingCompleted === false;
}
